// Delete grace period (ADR-0031): texts and small helpers for delete, undo, restore and "Delete now".
import type { DeleteImpact, GraceCollection, PendingDeletions } from "../api/types";
import { formatTime } from "./format";
import type { RouteType, TenantPage } from "./router";

/** The same value as DELETE_GRACE_SECONDS in apps/registry/src/plans.ts (3 days). */
export const DELETE_GRACE_SECONDS = 259200;

export const DELETED_TEXT = "Deleted. You can restore it for 3 days.";
export const API_KEY_LEAK_TEXT = "Do not restore a key that may have leaked. Create a new one instead.";
/** 409 on "Delete now" for a principal that published crate versions (QUESTIONS.md Q22). */
export const PURGE_BLOCKED_PRINCIPAL =
  "This principal published crate versions and stays deleted, but is kept for the version history.";
/** The same 409 for a trust config: one of its principals published crate versions. */
export const PURGE_BLOCKED_TRUST_CONFIG =
  "A principal of this trust config published crate versions. The trust config stays deleted, but is kept for the version history.";

/** The time a delete made now can be restored until. The cron purges on its first run after it. */
export function restoreDeadline(nowMs: number): string {
  return new Date(Math.floor(nowMs / 1000) * 1000 + DELETE_GRACE_SECONDS * 1000).toISOString();
}

/** Label of the delete button in the dialog (ADR-0031 section 10). */
export function deleteButtonLabel(nowMs: number): string {
  return `Delete (restore until ${formatTime(restoreDeadline(nowMs))})`;
}

/** "Restore until <time>." from the `purge_after` of the delete response, or null without one. */
export function restoreUntilText(purgeAfter: string | null | undefined): string | null {
  return purgeAfter ? `Restore until ${formatTime(purgeAfter)}.` : null;
}

/**
 * Who deleted an object: the member email or the principal name for the `usr_` or `prn_` ID, else the ID.
 * A principal gets "(principal)" after its name, so a machine delete is clear.
 */
export function actorLabel(
  id: string | null,
  users: ReadonlyMap<string, string>,
  principals: ReadonlyMap<string, string>,
): string {
  if (!id) return "unknown";
  const user = users.get(id);
  if (user) return user;
  const prn = principals.get(id);
  if (prn) return `${prn} (principal)`;
  return id;
}

/** The 409 text for "Delete now" when published versions block the purge. */
export function purgeBlockedText(collection: GraceCollection): string | null {
  if (collection === "principals") return PURGE_BLOCKED_PRINCIPAL;
  if (collection === "trust-configs") return PURGE_BLOCKED_TRUST_CONFIG;
  return null;
}

const PENDING_LABELS: {
  key: keyof PendingDeletions;
  one: string;
  many: string;
  page?: TenantPage;
  type?: RouteType;
}[] = [
  { key: "namespaces", one: "namespace", many: "namespaces", page: "namespaces" },
  { key: "trust_configs", one: "trust config", many: "trust configs", page: "trust-configs" },
  { key: "principals", one: "principal", many: "principals", page: "principals" },
  { key: "grants", one: "grant", many: "grants", page: "grants" },
  { key: "members", one: "member", many: "members", page: "members" },
  { key: "invites", one: "invite", many: "invites", page: "members" },
  { key: "domains", one: "domain", many: "domains", page: "settings" },
  // OCI (registry 0.18.0): tags and manifests are listed per repository, from the Containers view.
  {
    key: "oci_repositories",
    one: "container repository",
    many: "container repositories",
    page: "packages",
    type: "oci",
  },
  { key: "oci_manifests", one: "image manifest", many: "image manifests", page: "packages", type: "oci" },
  { key: "oci_tags", one: "image tag", many: "image tags", page: "packages", type: "oci" },
];

/** Non-zero counts of `pending_deletions`, as "1 trust config", "2 grants", in page order. */
export function pendingItems(
  p: PendingDeletions | undefined,
): { text: string; page?: TenantPage; type?: RouteType }[] {
  if (!p) return [];
  return PENDING_LABELS.flatMap(({ key, one, many, page, type }) => {
    const n = p[key];
    if (typeof n !== "number" || !Number.isInteger(n) || n <= 0) return [];
    return [{ text: `${n} ${n === 1 ? one : many}`, page, ...(type ? { type } : {}) }];
  });
}

/** The impact cannot be loaded: the dialog asks for the typed name to be safe. */
export const IMPACT_UNKNOWN_TEXT = "Prego cannot show what this delete affects. Type the name to confirm.";

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Lines for what a delete would cut off (api.md 2.8 impact). The first line says if it is in use. Counts that are
 * zero are not shown.
 */
export function impactLines(impact: DeleteImpact): string[] {
  const lines: string[] = [];
  const { principals, grants, cross_tenant_grants: cross, grantee_tenants: tenants } = impact;
  if (impact.in_use) {
    lines.push(
      impact.last_exchange_at
        ? `In use. The last token exchange was at ${formatTime(impact.last_exchange_at)}.`
        : "In use.",
    );
  } else if (impact.last_exchange_at) {
    lines.push(
      `Not in use in the last 7 days. The last token exchange was at ${formatTime(impact.last_exchange_at)}.`,
    );
  } else {
    lines.push("Not in use. No token exchange is recorded.");
  }
  if (principals) lines.push(`${plural(principals, "principal stops", "principals stop")} working.`);
  if (grants) lines.push(`${plural(grants, "grant stops", "grants stop")}.`);
  if (cross) {
    const who = tenants.length ? `: ${tenants.join(", ")}` : "";
    lines.push(`${plural(cross, "grant", "grants")} to other tenants stop${cross === 1 ? "s" : ""}${who}.`);
  }
  return lines;
}
